import moment from "moment";
import { DATE_TIME_FORMAT, DATE_FORMAT } from "@/constants/constant-values";

interface ToStandardDisplayDateParams {
  inputDate: string | null;
  inputFormat?: string;
  desiredFormat: "dateTime" | "time" | "localTimeIn24" | "date";
}

interface CommonDateTimeStringsParams {
  from_leave_date: string;
  from_leave_time: string;
  to_leave_date: string;
  to_leave_time: string;
  created_ts: string;
}

const dateHandler = () => {
  const toStandardDisplayDate = ({
    inputDate,
    inputFormat,
    desiredFormat,
  }: ToStandardDisplayDateParams): string => {
    if (!inputDate) return "N/A";
    switch (desiredFormat) {
      case "dateTime":
        return moment
          .utc(inputDate, inputFormat)
          .local()
          .format(DATE_TIME_FORMAT);
      case "time":
        return moment.utc(inputDate, inputFormat).local().format("hh:mm A");
      case "localTimeIn24":
        return moment.utc(inputDate, inputFormat).local().format("HH:mm:ss");
      default:
        return moment.utc(inputDate, inputFormat).local().format(DATE_FORMAT);
    }
  };

  const toDesiredFormat = (
    inputDate: string,
    inputFormat: string,
    desiredFormat: string,
  ): string => {
    if (!inputDate) return "N/A";
    return moment.utc(inputDate, inputFormat).local().format(desiredFormat);
  };

  const toStandardApiDate = (
    inputDate: string,
    inputFormat: string,
  ): string => {
    return moment.utc(inputDate, inputFormat).local().format("YYYY-MM-DD");
  };

  const formatDate = (dt: string = ""): string => {
    return moment
      .utc(dt, "DD-MM-YYYY HH:mm:ss")
      .local()
      .format("DD/MM/YYYY hh:mm A");
  };

  const formatDateOnly = (dt: string = ""): string => {
    return moment.utc(dt, "DD-MM-YYYY HH:mm:ss").local().format("MM-DD-YYYY");
  };

  const formatTimeOnly = (dt: string = ""): string => {
    return moment.utc(dt, "DD-MM-YYYY HH:mm:ss").local().format("hh:mm A");
  };

  const formatDateReverse = (date: string): string => {
    return moment.utc(date).local().format("YYYY-MM-DD");
  };

  const toDDMMYY = (date: string): string => {
    return moment.utc(date, "DD-MM-YYYY").local().format("DD/MM/YY");
  };

  const getMomentDate = (date: string): moment.Moment => {
    return moment.utc(date, "DD-MM-YYYY").local();
  };

  const getMdyToMoment = (date: string): moment.Moment => {
    return moment.utc(date, "MM-DD-YYYY").local();
  };

  const toTwoFourFormat = (
    period: string,
    hours: number,
    minutes: number,
  ): string => {
    if (period.toLowerCase() === "am" && hours === 12) hours = 0;
    if (period.toLowerCase() === "pm" && hours !== 12) hours += 12;
    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
  };

  const toOneTwoFormat = (time24hr: string): string => {
    const [hours, minutes] = time24hr.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) return "Invalid input";

    const period = hours >= 12 ? "pm" : "am";
    const formattedHours = hours % 12 || 12;
    return `${formattedHours}:${String(minutes).padStart(2, "0")} ${period}`;
  };

  const toTweleveHourFormat = (time24hr: string): string => {
    const [hours, minutes] = time24hr.split(":").map(Number);
    if (isNaN(hours) || isNaN(minutes)) return "Invalid input";

    const period = hours >= 12 ? "PM" : "AM";
    const formattedHours = hours % 12 || 12;
    return `${formattedHours}:${String(minutes).padStart(2, "0")} ${period}`;
  };

  const calculateMinutesBetweenTimes = (
    time1: string,
    time2: string,
  ): number => {
    const [hours1, minutes1] = time1.split(":").map(Number);
    const [hours2, minutes2] = time2.split(":").map(Number);
    return Math.abs(hours1 * 60 + minutes1 - (hours2 * 60 + minutes2));
  };

  const getCommonDateTimeStrings = (
    obj: CommonDateTimeStringsParams,
  ): Record<string, string> => {
    const [fromLeaveDay, fromLeaveMonth, fromLeaveDate] = ddmmyyToMonddyyyy(
      obj.from_leave_date,
    ).split(" ");
    const [toLeaveDay, toLeaveMonth, toLeaveDate] = ddmmyyToMonddyyyy(
      obj.to_leave_date,
    ).split(" ");
    return {
      fromFullDate: ddmmyyToMonddyyyy(obj.from_leave_date),
      fromLeaveTime: toTweleveHourFormat(obj.from_leave_time),
      fromLeaveDay,
      fromLeaveMonth,
      fromLeaveDate: fromLeaveDate.split(",")[0],
      toFullDate: ddmmyyToMonddyyyy(obj.to_leave_date),
      toLeaveTime: toTweleveHourFormat(obj.to_leave_time),
      toLeaveDay,
      toLeaveMonth,
      toLeaveDate: toLeaveDate.split(",")[0],
      createdDate: ddmmyyToMonddyyyy(obj.created_ts)
        .split(" ")
        .slice(1)
        .join(" "),
      createdTime: toTweleveHourFormat(obj.created_ts.split(" ")[1]),
    };
  };

  const ddmmyyToMonddyyyy = (inputDate: string): string => {
    return moment
      .utc(inputDate, "DD-MM-YYYY HH:mm:ss")
      .local()
      .format("dddd MMM D, YYYY");
  };

  const convertToYYYYMMDD = (dateString: string): string => {
    return moment
      .utc(dateString, "ddd MMM DD YYYY HH:mm:ss [GMT]Z (zz)", "en")
      .local()
      .format("YYYY-MM-DD");
  };

  return {
    toStandardDisplayDate,
    toDesiredFormat,
    toStandardApiDate,
    formatDate,
    formatDateReverse,
    toTwoFourFormat,
    toOneTwoFormat,
    toTweleveHourFormat,
    toDDMMYY,
    getMomentDate,
    getMdyToMoment,
    calculateMinutesBetweenTimes,
    ddmmyyToMonddyyyy,
    getCommonDateTimeStrings,
    convertToYYYYMMDD,
    formatDateOnly,
    formatTimeOnly,
  };
};

export default dateHandler;
